import { cloneDeep } from 'lodash';
import takeOrderMenu from 'takeorder-menu-js';

import breakpoints from '@/plugins/breakpoints';
import dayjs from 'dayjs';

export function bottomVisible() {
  const { scrollY } = window;
  const visible = document.documentElement.clientHeight;
  const pageHeight = document.documentElement.scrollHeight;
  const bottomOfPage = visible + scrollY >= pageHeight;
  return bottomOfPage || pageHeight < visible;
}

export function calculateCartTotal(items = []) {
  return items
    .map(({ total }) => total)
    .reduce((pre, cur) => pre + cur, 0);
}

export function countCartItems(items = []) {
  return items
    .map(({ count }) => count)
    .reduce((pre, cur) => pre + cur, 0);
}

export function getCart() {
  const defaultCart = {
    count: 0,
    items: [],
    menu_id: null,
    total: 0,
  };
  const localCart = localStorage.getItem('cart') || JSON.stringify(defaultCart);
  const parseCart = JSON.parse(localCart);

  return parseCart;
}

export function getMenuTopToContentHeight() {
  const scrollbar = document.getElementById('menu-scrollbar');
  const headerHeight = breakpoints.sm ? 102 : 96;
  const scrollbarHeight = scrollbar?.offsetHeight || 0;
  const scrollbarTop = scrollbar?.offsetTop || 0;

  return headerHeight + scrollbarTop + scrollbarHeight + 24;
}

export function getTime(target = new Date()) {
  const time = new Date(target);
  const prefix = (val) => `0${val}`.slice(-2);

  const day = prefix(time.getDate());
  const month = prefix(time.getMonth() + 1);
  const year = time.getFullYear();

  const hours = prefix(time.getHours());
  const minutes = prefix(time.getMinutes());
  const seconds = prefix(time.getSeconds());

  const weekday = time.getDay();

  return {
    day,
    hours,
    minutes,
    month,
    seconds,
    weekday,
    year,
  };
}

export function getTimeData(formattedTime = '', showYear = false) {
  const formatted = getTime(formattedTime) || {};
  const today = new Date();
  const {
    day: curDay,
    month: curMonth,
    year: curYear,
  } = getTime(today) || {};
  const tomorrow = new Date(today).setDate(today.getDate() + 1);
  const {
    day: tomorrowDay,
    month: tomorrowMonth,
    year: tomorrowYear,
  } = getTime(tomorrow);
  const {
    day,
    month,
    year,
    hours,
    minutes,
    weekday,
  } = formatted;

  let dateString = showYear ? `${year}/${month}/${day}` : `${month}/${day}`;
  let weekdayString = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'][weekday];
  if (curYear === year && curMonth === month && curDay === day) {
    dateString = '今天';
    weekdayString = '';
  } else if (tomorrowYear === year && tomorrowMonth === month && tomorrowDay === day) {
    dateString = '明天';
    weekdayString = '';
  }

  return {
    ...formatted,
    dateString,
    timeString: `${hours}:${minutes}`,
    weekdayString,
  };
}

export function formatMemberOrders(res = {}) {
  const {
    member_orders: memberOrders = [],
  } = res;
  return memberOrders.map(({
    canceled_at: canceledAt,
    created_at: createdAt = {},
    receive_time: receiveTime = {},
    ...rest
  } = {}) => ({
    ...rest,
    // NOTE: fit the prop names of displayed-time-format
    canceled_at: canceledAt ? {
      start_date: getTimeData(canceledAt).dateString,
      start_time: getTimeData(canceledAt).timeString,
    } : undefined,
    // NOTE: fit the prop names of displayed-time-format
    created_at: {
      start_date: getTimeData(createdAt).dateString,
      start_time: getTimeData(createdAt).timeString,
    },
    // NOTE: fit the prop names of displayed-time-format
    receive_time: {
      ...receiveTime,
      end_date: getTimeData(receiveTime.end_time).dateString,
      end_time: getTimeData(receiveTime.end_time).timeString,
      start_date: getTimeData(receiveTime.start_time).dateString,
      start_time: getTimeData(receiveTime.start_time).timeString,
    },
  }));
}

export function getWeekString(formattedTime) {
  const formatted = dayjs(formattedTime) || {};
  const today = dayjs();
  const tomorrow = dayjs().add(1, 'day');

  if (today.isSame(formatted, 'day')) {
    return '今天';
  }
  if (tomorrow.isSame(formatted, 'day')) {
    return '明天';
  }
  return {
    0: '週日',
    1: '週一',
    2: '週二',
    3: '週三',
    4: '週四',
    5: '週五',
    6: '週六',
  }[formatted.day()];
}

function sanitizeHtml(str) {
  if (!str) return str;
  const decoded = str
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, code) => String.fromCharCode(parseInt(code, 16)));
  const doc = new DOMParser().parseFromString(decoded, 'text/html');
  doc.querySelectorAll('script, iframe, object, embed, form').forEach((el) => el.remove());
  doc.querySelectorAll('*').forEach((el) => {
    [...el.attributes].forEach(({ name }) => {
      if (/^on/i.test(name) || (name === 'href' && /^javascript:/i.test(el.getAttribute(name)))) {
        el.removeAttribute(name);
      }
    });
  });
  return doc.body.innerHTML;
}

export function formatMenu(menu = {}) {
  const buildEntreeLimit = (data) => {
    if (!data) {
      return {};
    }
    return Object.keys(data).reduce((mapping, entreeIndex) => {
      const entreeLimit = data[entreeIndex];
      const { current = 0, limit = 0 } = entreeLimit || {};
      return {
        ...mapping,
        [entreeIndex]: {
          ...entreeLimit,
          available: limit - current,
        },
      };
    }, {});
  };

  const {
    end_time: expiredEnd,
    joined,
    limit,
    limit_amount: limitAmount,
    name,
    note,
    start_time: expiredStart,
    status,
    type,
    store_name: storeName,
    store_url: storeUrl,
    team_group_buying_receive_times: allowedPeriods,
    menu: menuItems,
    entree_limit: entreeLimit,
    ...payload
  } = menu;

  const result = {
    ...payload,
    status,
    infos: {
      allowed_receive_periods: allowedPeriods?.map((period) => ({
        id: period.id,
        end_date: getTimeData(period?.end_time).dateString,
        end_time: getTimeData(period?.end_time).timeString,
        end_weekday: getTimeData(period?.end_time).weekdayString,
        start_date: getTimeData(period?.start_time).dateString,
        start_time: getTimeData(period?.start_time).timeString,
        start_weekday: getTimeData(period?.start_time).weekdayString,
      })),
      expired_periods: [
        {
          start_date: getTimeData(expiredEnd).dateString,
          start_time: getTimeData(expiredEnd).timeString,
          start_weekday: getTimeData(expiredEnd).weekdayString,
        },
        {
          end_date: getTimeData(expiredEnd).dateString,
          end_time: getTimeData(expiredEnd).timeString,
          end_weekday: getTimeData(expiredEnd).weekdayString,
          start_date: getTimeData(expiredStart).dateString,
          start_time: getTimeData(expiredStart).timeString,
          start_weekday: getTimeData(expiredStart).weekdayString,
        },
      ],
      limit,
      limit_amount: limitAmount,
      name,
      note: sanitizeHtml(note),
      status,
      type,
      joined,
      store_name: storeName,
      store_url: storeUrl,
    },
    items: takeOrderMenu.build({ input: menuItems }),
    entree_limit: buildEntreeLimit(entreeLimit),
  };

  return result;
}

export function formatMenuList(menuList = []) {
  return menuList
    .map((menu) => {
      const {
        member_orders: memberOrders = [],
        receive_times: receiveTimes = [],
        ...payload
      } = menu;
      return {
        ...payload,
        receive_times: cloneDeep(receiveTimes).sort((a, b) => (dayjs(a.start_time).isAfter(dayjs(b.start_time), 'minute') ? 1 : -1)),
        member_orders: cloneDeep(memberOrders).sort((a, b) => {
          const { receive_time: aReceiveTime, created_at: aCreatedAt = '' } = a;
          const { receive_time: bReceiveTime, created_at: bCreatedAt = '' } = b;
          const aReceiveFormat = dayjs(aReceiveTime.start_time);
          const bReceiveFormat = dayjs(bReceiveTime.start_time);
          const aCreatedAtFormat = dayjs(aCreatedAt);
          const bCreatedAtFormat = dayjs(bCreatedAt);
          if (aReceiveFormat.isSame(bReceiveFormat, 'minute')) {
            return aCreatedAtFormat.isAfter(bCreatedAtFormat, 'minute') ? 1 : -1;
          }
          return aReceiveFormat.isAfter(bReceiveFormat, 'minute') ? 1 : -1;
        }),
      };
    })
    .sort((a, b) => {
      const aReceiveTime = a.receive_times[0];
      const bReceiveTime = b.receive_times[0];
      return dayjs(aReceiveTime.start_time).isAfter(dayjs(bReceiveTime.start_time), 'minute') ? 1 : -1;
    });
}

export function numberInputHandler(event) {
  const keyCode = event.keyCode || event.which;
  const RESTRICTED_KEYCODES = [
    69, // NOTE: e
    109, // NOTE: Numpad -
    110, // NOTE: Numpad .
    189, // NOTE: -
    190, // NOTE: .
  ];
  if (RESTRICTED_KEYCODES.includes(keyCode)) {
    event.preventDefault();
    event.stopImmediatePropagation();
  }
}

export function roundToTwo(num) {
  return +`${Math.round(`${num}e+2`)}e-2`;
}

export default {
  bottomVisible,
  calculateCartTotal,
  countCartItems,
  formatMenu,
  getCart,
  getMenuTopToContentHeight,
  getTimeData,
  numberInputHandler,
  roundToTwo,
};
